<template>
    <EditWindow v-if="data.show_payments_window" heading="Payments" :name="data.payments_student.ui" :width="1180"
                @close="close_payments_window()">

        <div class="pay-toolbar">
            <button type="button" class="pay-btn pay-btn-primary" title="Add a new, empty payment for this student"
                    @click.stop="create_payment(data.payments_student)">
                <span class="material-symbols-outlined" aria-hidden="true">add_card</span>
                New payment
            </button>
            <button type="button" class="pay-btn" title="Show this student's classes that are not linked to any payment"
                    @click.stop="get_null_attendances(data.payments_student)">
                <span class="material-symbols-outlined" aria-hidden="true">link_off</span>
                Unlinked classes
            </button>
            <button type="button" class="pay-btn" title="Re-order the packages and re-link classes to them by package hours (asks first)"
                    @click.stop="fix_attendance_package_relation(data.payments_student)">
                <span class="material-symbols-outlined" aria-hidden="true">build</span>
                Fix package order
            </button>
        </div>

        <p v-if="!data.student_payments || !Object.keys(data.student_payments).length" class="pay-empty">No payments yet.</p>

        <div v-for="(payment,payment_index) in data.student_payments" :key="payment.id ?? `p${payment_index}`"
             class="pay-card" :class="{open:payment.show, paid:payment.paid}">

            <div class="pay-card-head">
                <button type="button" class="pay-expand" :aria-expanded="!!payment.show" @click.stop="toggle_payment(payment)">
                    <span class="material-symbols-outlined pay-chevron" aria-hidden="true">chevron_right</span>
                    <span class="pay-card-title">Payment {{ payment_index+1 }}</span>
                    <span class="pay-badge" :class="payment.paid ? 'ok' : 'due'">{{ payment.paid ? 'Paid' : 'Not paid' }}</span>
                    <span class="pay-summary">{{ [payment.plan, payment.amount, payment.datetime && payment.datetime.slice(0,10)].filter(Boolean).join(' · ') }}</span>
                </button>
                <IconButton icon="delete" label="Remove" danger tip="Remove this payment (asks first)"
                            @click.stop="remove_payment(payment)"/>
            </div>

            <div v-if="payment.show" class="pay-card-body">

                <div class="pay-fields">
                    <label>Date & time<input @change.stop="patch(payment,'datetime')" type="datetime-local" v-model="payment.datetime"></label>
                    <label>Plan<input @change.stop="patch(payment,'plan')" v-model="payment.plan"></label>
                    <label>Amount<input @change.stop="patch(payment,'amount')" v-model="payment.amount" inputmode="decimal"></label>
                    <label>Method<input @change.stop="patch(payment,'method')" v-model="payment.method"></label>
                    <label>Reference<input @change.stop="patch(payment,'reference')" v-model="payment.reference"></label>
                    <label>Status<input @change.stop="patch(payment,'status')" v-model="payment.status"></label>
                    <label class="wide">Comments<input @change.stop="patch(payment,'comments')" v-model="payment.comments"></label>
                    <label class="pay-check"><input type="checkbox" @change.stop="patch(payment,'paid')" v-model="payment.paid"> Paid</label>
                </div>

                <div class="pay-section-head">
                    <span class="pay-section-title">Packages</span>
                    <button type="button" class="pay-btn" title="Add a new package to this payment"
                            @click.stop="create_package(payment,data.payments_student)">
                        <span class="material-symbols-outlined" aria-hidden="true">add</span>
                        New package
                    </button>
                </div>

                <div v-for="(package_,package_index) in payment.package" :key="package_.id" class="pkg-card">

                    <div class="pkg-head">
                        <span class="pkg-title">Package {{ package_index+1 }}</span>
                        <div class="pkg-hours">
                            <label>Taken hours
                                <input @change.stop="patch(package_, 'taken_hours')" type="number" step="0.01" min="0" v-model="package_.taken_hours">
                            </label>
                            <label>Package hours
                                <input @change.stop="patch(package_, 'package_hours')" type="number" step="0.01" min="0" v-model="package_.package_hours">
                            </label>
                        </div>
                        <div class="pay-actions">
                            <IconButton icon="calendar_month" label="Monthly"
                                tip="Make this a monthly package: pull in the payment month's classes (asks first)"
                                @click.stop="make_monthly(package_,data.payments_student)"/>
                            <IconButton icon="delete" label="Remove" danger tip="Remove this package (asks first)"
                                @click.stop="remove_package(payment,package_)"/>
                        </div>
                    </div>

                    <div class="pay-section-head">
                        <span class="pay-section-title">Classes ({{ package_.attendance ? package_.attendance.length : 0 }})</span>
                        <button type="button" class="pay-btn" title="Add a new class to this package"
                                @click.stop="create_attendance(package_)">
                            <span class="material-symbols-outlined" aria-hidden="true">add</span>
                            New class
                        </button>
                    </div>

                    <div v-for="attendance in package_.attendance" :key="attendance.id" class="att-card">

                        <div class="att-head">
                            <label class="pay-check" title="Ticked once an admin has checked this class">
                                <input @change.stop="patch(attendance,'admin_check')" type="checkbox" v-model="attendance.admin_check">
                                Admin checked
                            </label>
                            <IconButton icon="delete" label="Delete" danger tip="Delete this class (asks first)"
                                        @click.stop="remove_attendance(package_,attendance)"/>
                        </div>

                        <div class="pay-fields">
                            <label>Date & time<input @change.stop="patch(attendance,'datetime')" type="datetime-local" v-model="attendance.datetime"></label>
                            <label>Duration<input @change.stop="patch(attendance,'duration')" type="number" step="0.01" v-model="attendance.duration"></label>
                            <label>Payment / package
                                <select @change.stop="change_payment_package($event,payment,package_,attendance)">
                                    <template v-for="(paym,paym_index) in data.student_payments">
                                        <template v-for="(pack,pack_index) in paym.package">
                                            <template v-if="pack.id==package_.id">
                                                <option :value="`${paym.id} | ${pack.id}`"  selected> Payment {{paym_index+1}} | Package {{  pack_index+1 }}</option>
                                            </template>
                                            <template v-else>
                                                <option :value="`${paym.id} | ${pack.id}`"> Payment{{paym_index+1}} | Package {{  pack_index+1 }}</option>
                                            </template>
                                        </template>
                                    </template>
                                </select>
                            </label>
                            <label>Group
                                <select @change.stop="patch_group($event,attendance,'group')">
                                    <option value=""></option>
                                    <option v-for="group in (data.payment_groups||[]).filter(Boolean)" :key="group.id" :value="group.id"
                                            :selected="!!attendance.group && group.id==attendance.group.id">{{ group.id }}</option>
                                </select>
                            </label>
                            <label>Link<input @change.stop="patch(attendance,'link')" v-model="attendance.link"></label>
                            <label>Video link<input @change.stop="patch(attendance,'video_link')" v-model="attendance.video_link"></label>
                            <label class="wide">Comments<input @change.stop="patch(attendance,'comments')" v-model="attendance.comments"></label>
                        </div>

                        <div class="conf-columns">
                            <div class="conf-col">
                                <div class="pay-section-head">
                                    <span class="pay-section-title">Instructor confirmations</span>
                                    <IconButton icon="add" label="Add" tip="Add an instructor confirmation"
                                                @click.stop="create_instructor_confirmation(attendance)"/>
                                </div>
                                <div v-for="instructor_confirmation in attendance.instructor_confirmation" :key="instructor_confirmation.id" class="conf-row">
                                    <span class="conf-meta">{{instructor_confirmation.datetime}} · #{{instructor_confirmation.id}}</span>
                                    <template v-if="instructor_confirmation.instructor">
                                        <select @change.stop="patch_instructor($event,instructor_confirmation,'instructor')" aria-label="Instructor">
                                            <option value=""></option>
                                            <template v-for="instructor in data.payment_instructors">
                                                <template v-if="instructor.id==instructor_confirmation.instructor.id">
                                                    <option selected  :value="instructor.id">{{instructor.ui}}</option>
                                                </template>
                                                <template v-else>
                                                    <option  :value="instructor.id">{{instructor.ui}}</option>
                                                </template>
                                            </template>
                                        </select>
                                    </template>
                                    <template v-else>
                                        <select @change.stop="patch_instructor($event,instructor_confirmation,'instructor')" aria-label="Instructor">
                                            <option value=""></option>
                                            <template v-for="instructor in data.payment_instructors">
                                                <option :value="instructor.id">{{instructor.ui}}</option>
                                            </template>
                                        </select>
                                    </template>
                                    <select @change.stop="patch(instructor_confirmation,'confirmation')" v-model=instructor_confirmation.confirmation aria-label="Status">
                                        <option v-for="status in status_data.instructor">{{ status }}</option>
                                    </select>
                                    <IconButton icon="close" label="Remove" danger tip="Remove this instructor confirmation (asks first)"
                                                @click.stop="remove_instructor_confirmation(attendance,instructor_confirmation)"/>
                                </div>
                            </div>

                            <div class="conf-col">
                                <div class="pay-section-head">
                                    <span class="pay-section-title">Student confirmations</span>
                                    <IconButton icon="add" label="Add" tip="Add student confirmations"
                                                @click.stop="create_student_confirmation(attendance)"/>
                                </div>
                                <div v-for="student_confirmation in attendance.student_confirmation" :key="student_confirmation.id" class="conf-row">
                                    <span class="conf-meta">{{ student_confirmation.datetime }} · #{{ student_confirmation.id }}</span>
                                    <template v-if="student_confirmation.student">
                                        <select @change.stop="patch_student($event,student_confirmation,'student')" aria-label="Student">
                                            <option value=""></option>
                                            <template v-for="student in data.payment_students">
                                                <template v-if="student.id==student_confirmation.student.id">
                                                    <option selected  :value="student.id">{{student.ui}}</option>
                                                </template>
                                                <template v-else>
                                                    <option  :value="student.id">{{student.ui}}</option>
                                                </template>
                                            </template>
                                        </select>
                                    </template>
                                    <template v-else>
                                        <select @change.stop="patch_student($event,student_confirmation,'student')" aria-label="Student">
                                            <option value=""></option>
                                            <template v-for="student in data.payment_students">
                                                <option :value="student.id">{{student.ui}}</option>
                                            </template>
                                        </select>
                                    </template>
                                    <select @change.stop="patch(student_confirmation,'confirmation')" v-model=student_confirmation.confirmation aria-label="Status">
                                        <option v-for="status in status_data.student">{{ status }}</option>
                                    </select>
                                    <IconButton icon="close" label="Remove" danger tip="Remove this student confirmation (asks first)"
                                                @click.stop="remove_student_confirmation(attendance,student_confirmation)"/>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </div>

    </EditWindow>
</template>


<script setup>
    import {onMounted,reactive,ref, inject} from 'vue'
    import axios from 'axios'
    import {useStore} from 'vuex'
    import EditWindow from '@/components/edit/EditWindow.vue'
    import IconButton from '@/components/IconButton.vue'
    import {ask} from '@/confirm'
    
    
    let data=inject('data')

    let store=useStore()

    let status_data=reactive({
        student:[],
        instructor:[],
    })

    // let confirmations=ref(['pending','confirmed','cancelled-late','no-show','rescheduled','cancelled-on-time','cancelled-by-student','cancelled-on-time-by-student','cancelled-late-by-student'])

    onMounted(async ()=>{
        
        await get_statuses()
    })

    async function get_statuses(){
        let response=await axios.post('/variables/get_statuses/')

        status_data.student=response.data.student
        status_data.instructor=response.data.instructor

    }


    // Runs once the window has finished closing (EditWindow animates it out first).
    function close_payments_window(){
        data.hide_payments_window=true
        data.show_payments_window=false
        data.student_payments={}
        data.payments_student={}
    }



    async function create_payment(student){
        let response=await axios.post('/payments/create_payment/',{student_id:student.id})
        let new_payment=response.data
        data.student_payments.unshift(new_payment)
    }

    async function remove_payment(payment){
        let confirm_delete=await ask({title:'Remove this payment?', message:'This cannot be undone.', confirm_label:'Remove', danger:true, icon:'delete'})
        if (!confirm_delete){
            return
        }
        let response=await axios.post('/payments/remove_payment/',{payment_id:payment.id})
        for (let paym of data.student_payments){
            if(paym.id==payment.id){
                const index=Object.values(data.student_payments).indexOf(paym)
                data.student_payments.splice(index,1)
                break
            }

        }
    }


    async function get_null_attendances(student){
        let response= await axios.post('/payments/get_null_attendances/',{student_id:student.id})
        let null_payment=response.data
        data.student_payments.push(null_payment)
    }


    async function create_package(payment){
        let response=await axios.post('/payments/create_package/',{payment_id:payment.id,student_id:data.payments_student.id})
        let new_package=response.data
        payment.package.unshift(new_package)

    }

    async function remove_package(payment,package_){
        let confirm_delete=await ask({title:'Remove this package?', message:'This cannot be undone.', confirm_label:'Remove', danger:true, icon:'delete'})
        if (!confirm_delete){
            return
        }


        let response=await axios.post('/payments/remove_package/',{package_id:package_.id})
        for(let pack of payment.package){

            if (pack.id==package_.id){
                const index=Object.values(payment.package).indexOf(pack)
                payment.package.splice(index,1)

            }
        }
    }


    async function create_attendance(package_){
        let response= await axios.post('/payments/create_attendance/',{package_id:package_.id})
        let attendance=response.data
        package_.attendance.unshift(attendance)

    }


    async function remove_attendance(package_,attendance){

        let confirm_delete=await ask({title:'Remove this class?', message:'This cannot be undone.', confirm_label:'Remove', danger:true, icon:'delete'})
        if (!confirm_delete){
            return
        }


        let response=await axios.post('/payments/remove_attendance/',{attendance_id:attendance.id})
        for (let att of package_.attendance){
            if (att.id==attendance.id){
                const index=Object.values(package_.attendance).indexOf(att)
                package_.attendance.splice(index,1)
            }
        }

    }


    async function make_monthly(package_,student){
        let yes_no=await ask({
            title:'Make this package monthly?',
            icon:'calendar_month',
            message:"All of the payment month's classes are moved into this package, and its package hours are set to its taken hours. Classes from other months are not removed; they are just brought into the package.",
            warning:"If the payment method contains '@end', the month before the payment month is used (a January 2024 payment takes December 2023's classes).",
            confirm_label:'Make monthly',
        })
        if (yes_no){
            let response=await axios.post('/payments/make_monthly/',{package_id:package_.id,student_id:student.id})
            Object.assign(package_,response.data)
        }

    }

    async function fix_attendance_package_relation(student){
        let yes_no=await ask({
            title:'Fix package order?',
            icon:'build',
            message:"All of this student's packages are re-arranged and put in order by their package hours, and the classes are re-linked to them.",
            confirm_label:'Fix packages',
        })
        if (yes_no){
            let response=await axios.post('/payments/fix_attendance_package_relation/',{student_id:student.id})
            Object.assign(data.student_payments,response.data)
        }
    }




 async function change_payment_package(event,payment,package_,attendance){

        let payment_package_string=event.target.value

        let [payment_id,package_id]=payment_package_string.split(' | ')

        let response=await axios.post('/payments/change_payment_package/',{old_payment_id:payment.id,old_package_id:package_.id,payment_id:payment_id,package_id:package_id,attendance_id:attendance.id})
        
        package_.attendance = package_.attendance.filter(att => att.id !== attendance.id);


        for (let paym of data.student_payments){
            if (paym.id == payment_id){
                for(let pack of paym.package){
                    if(pack.id==package_id){
                        pack.attendance.unshift(attendance)
                        break
                    }
                }
                break
            }
        }

        
        for (let option of event.target.options){
            let value=option.value
            let [paym_id,pack_id]=value.split(' | ')
            if (paym_id==payment.id && pack_id==package_.id){
                option.selected=true
            }
        }


    }




    async function create_instructor_confirmation(attendance){
        let response=await axios.post('/payments/create_instructor_confirmation/',{group_id:data.emergency_group.id,attendance_id:attendance.id})

        let instructor_confirmation=response.data

        attendance.instructor_confirmation.push(instructor_confirmation)

    }

    async function remove_instructor_confirmation(attendance,instructor_confirmation){


        let confirm_delete=await ask({title:'Remove this instructor confirmation?', message:'This cannot be undone.', confirm_label:'Remove', danger:true, icon:'delete'})
        if (!confirm_delete){
            return
        }


        let response = await axios.post('/payments/remove_instructor_confirmation/',{instructor_confirmation_id:instructor_confirmation.id})


        attendance.instructor_confirmation=attendance.instructor_confirmation.filter(inst_conf=>inst_conf.id!=instructor_confirmation.id)


    }

    async function create_student_confirmation(attendance){
        let response=await axios.post('/payments/create_student_confirmation/',{group_id:data.emergency_group.id,attendance_id:attendance.id})

        let student_confirmation=response.data

        // attendance.student_confirmation.push(student_confirmation)

        Object.assign(attendance.student_confirmation,response.data)



    }

    async function remove_student_confirmation(attendance,student_confirmation){

        let confirm_delete=await ask({title:'Remove this student confirmation?', message:'This cannot be undone.', confirm_label:'Remove', danger:true, icon:'delete'})
        if (!confirm_delete){
            return
        }


        let response=await axios.post('/payments/remove_student_confirmation/',{student_confirmation_id:student_confirmation.id})


        attendance.student_confirmation=attendance.student_confirmation.filter(stud_conf=>stud_conf.id!=student_confirmation.id)

    }

    async function patch_group(event,element,key){
        let value=event.target.value
        let response=await axios.patch('/payments/patch/',{object:element,key:key,value:value}) 
        Object.assign(element,response.data)
    }

    async function patch_instructor(event,element,key){
        let value=event.target.value
        let response=await axios.patch('/payments/patch/',{object:element,key:key,value:value}) 
        Object.assign(element,response.data)
    }

    async function patch_student(event,element,key){
        let value=event.target.value
        let response=await axios.patch('/payments/patch/',{object:element,key:key,value:value}) 
        Object.assign(element,response.data)
    }

    async function patch(element,key){
        let value=element[key]
        let response=await axios.patch('/payments/patch/',{object:element,key:key,value:value}) 
        Object.assign(element,response.data)
    }

    async function toggle_payment(payment){
        if (payment.show){
            payment.hide=true
            payment.show=false
        }

        else{
            let response=await axios.post('/payments/get_packages/',{payment_id:payment.id})
            payment.package=response.data
            payment.show=true
            payment.hide=false
        }
        

    }


    

</script>

<style scoped>

    .pay-toolbar,
    .pay-section-head{
        display:flex;
        flex-wrap:wrap;
        align-items:center;
        gap:8px;
    }
    .pay-section-head{ justify-content:space-between; }
    .pay-section-title{
        font-size:12px;
        font-weight:600;
        letter-spacing:0.05em;
        text-transform:uppercase;
        color:var(--ew-muted);
    }

    .pay-btn{
        display:inline-flex;
        align-items:center;
        gap:6px;
        width:auto;
        height:38px;
        min-height:38px;
        padding:0 12px;
        font-size:14px;
        font-weight:600;
        word-break:normal;
        color:var(--ew-focus);
        background:var(--ew-card);
        border:1px solid var(--ew-line);
        border-radius:9px;
        box-shadow:none;
    }
    .pay-btn:hover{
        color:#055a7c;
        background:rgba(6,115,158,0.08);
        box-shadow:none;
    }
    .pay-btn-primary{
        color:#ffffff;
        background:var(--ew-focus);
        border-color:var(--ew-focus);
    }
    .pay-btn-primary:hover{
        color:#ffffff;
        background:#055a7c;
    }
    .pay-btn .material-symbols-outlined{ font-size:20px; }

    .pay-empty{
        margin:0;
        color:var(--ew-muted);
        font-style:italic;
    }

    /* ---- payment ---- */
    .pay-card{
        background:var(--ew-card);
        border:1px solid var(--ew-line);
        border-left:4px solid #c9a227;
        border-radius:10px;
    }
    .pay-card.paid{ border-left-color:var(--ew-ok); }
    .pay-card-head{
        display:flex;
        align-items:center;
        gap:4px;
        padding:4px 6px;
    }
    .pay-expand{
        display:flex;
        flex:1 1 auto;
        flex-wrap:wrap;
        align-items:center;
        gap:4px 8px;
        min-width:0;
        width:auto;
        min-height:44px;
        padding:4px 6px;
        word-break:normal;
        text-align:left;
        color:var(--ew-text);
        background:transparent;
        border:none;
        border-radius:8px;
        box-shadow:none;
    }
    .pay-expand:hover{
        color:var(--ew-text);
        background:rgba(6,115,158,0.08);
        box-shadow:none;
    }
    .pay-chevron{
        color:var(--ew-muted);
        transition:transform 0.2s ease;
    }
    .pay-card.open .pay-chevron{ transform:rotate(90deg); }
    .pay-card-title{ font-weight:600; font-size:15px; }
    .pay-summary{ color:var(--ew-muted); font-size:13px; }
    .pay-badge{
        padding:1px 8px;
        font-size:11px;
        font-weight:600;
        letter-spacing:0.04em;
        text-transform:uppercase;
        border-radius:999px;
    }
    .pay-badge.ok{ color:#1f6b55; background:rgba(42,138,110,0.14); }
    .pay-badge.due{ color:#7a4f00; background:#fff1cc; }

    .pay-card-body{
        display:flex;
        flex-direction:column;
        gap:12px;
        padding:4px 12px 12px;
        border-top:1px solid var(--ew-line);
    }

    /* ---- label-over-input grids ---- */
    .pay-fields{
        display:grid;
        grid-template-columns:repeat(auto-fill,minmax(min(100%,170px),1fr));
        gap:10px 12px;
        padding-top:8px;
    }
    .pay-fields .wide{ grid-column:1 / -1; }
    .pay-fields label,
    .pkg-hours label{
        display:flex;
        flex-direction:column;
        gap:4px;
        min-width:0;
        font-size:12px;
        color:var(--ew-muted);
    }
    .pay-fields input:not([type=checkbox]),
    .pay-fields select,
    .pkg-hours input,
    .conf-row select{
        width:100%;
        min-width:0;
        height:38px;
        padding:0 10px;
        font-size:14px;
        color:var(--ew-text);
        background:#ffffff;
        border:1px solid var(--ew-field-line);
        border-radius:8px;
    }
    .pay-fields input:focus,
    .pay-fields select:focus,
    .pkg-hours input:focus,
    .conf-row select:focus{
        outline:2px solid var(--ew-focus);
        outline-offset:-1px;
        border-color:var(--ew-focus);
    }
    label.pay-check{
        flex-direction:row;
        align-items:center;
        gap:8px;
        font-size:14px;
        color:var(--ew-text);
        cursor:pointer;
    }
    .pay-check input{
        width:20px;
        height:20px;
        accent-color:var(--ew-ok);
    }
    .pay-fields .pay-check{ align-self:end; min-height:38px; }

    /* ---- package ---- */
    .pkg-card{
        display:flex;
        flex-direction:column;
        gap:10px;
        padding:10px;
        background:var(--ew-bg);
        border:1px solid var(--ew-line);
        border-radius:10px;
    }
    .pkg-head{
        display:flex;
        flex-wrap:wrap;
        align-items:flex-end;
        justify-content:space-between;
        gap:8px 14px;
    }
    .pkg-title{ align-self:center; font-weight:600; }
    .pkg-hours{
        display:flex;
        flex:1 1 280px;
        gap:10px;
    }
    .pkg-hours label{ flex:1 1 0; }
    .pay-actions{ display:flex; gap:2px; }

    /* ---- class (attendance) ---- */
    .att-card{
        display:flex;
        flex-direction:column;
        gap:8px;
        padding:8px 10px 10px;
        background:var(--ew-card);
        border:1px solid var(--ew-line);
        border-radius:8px;
    }
    .att-head{
        display:flex;
        align-items:center;
        justify-content:space-between;
        gap:8px;
    }

    .conf-columns{
        display:grid;
        grid-template-columns:repeat(auto-fit,minmax(min(100%,360px),1fr));
        gap:10px 16px;
    }
    .conf-col{
        display:flex;
        flex-direction:column;
        gap:6px;
    }
    .conf-row{
        display:grid;
        grid-template-columns:1fr 1fr auto;
        align-items:center;
        gap:4px 6px;
        padding-top:6px;
        border-top:1px dashed var(--ew-line);
    }
    .conf-meta{
        grid-column:1 / -1;
        font-size:12px;
        color:var(--ew-muted);
    }

    @media (max-width:600px){
        .pay-toolbar .pay-btn{ flex:1 1 auto; justify-content:center; }
        .pay-card-body{ padding:4px 8px 10px; }
        .pkg-card{ padding:8px; }
        .pkg-hours{ flex-basis:100%; }
        .att-card{ padding:8px; }
        .conf-row{ grid-template-columns:1fr auto; }
        .conf-row select{ grid-column:1; }
        .conf-row > :last-child{ grid-column:2; grid-row:2 / span 2; }
    }

</style>
